import React, { useState, useEffect } from "react";
import { Grid } from "@mui/material";
import axios from "axios";
import CodeDialog from "./../dialog/codeDdialog";
import { landingLog, parseJsonOrRawString } from "../../utils/landingLog";
import {
	isValidEmail,
	isValidNormalizedPhone,
	isValidPhoneInput,
	normalizeEmail,
	normalizePhone,
} from "../../utils/contactUtils";
import { isLikelyCorsOrOpaqueNetworkReadBlocked } from "../../utils/corsDetection";

import Header from "./components/Header";
import BasicDetails from "./components/BasicDetails";
import ContactDetails from "./components/ContactDetails";
import AdditionalDetails from "./components/AdditionalDetails";
import NotificationPreferences from "./components/NotificationPreferences";
import ProfileImage from "./components/ProfileImage";
import Footer from "./components/Footer";
import FormCard from "./components/FormCard";
import { useStyles } from "./components/styles";

/** Matchmaker / landing API sometimes returns plain text (e.g. `OK`); avoid axios JSON parse errors on 2xx. */
const jsonOrTextResponse = { transformResponse: [parseJsonOrRawString] };

const getLambdaDomain = () => {
	/** Same-origin or custom host (e.g. reverse proxy); avoids browser CORS when set to the page origin. */
	const explicit = (process.env.REACT_APP_MATCHMAKER_API_BASE || "").trim().replace(/\/$/, "");
	if (explicit) {
		return explicit;
	}
	const params = new URLSearchParams(window.location.search);
	const env = params.get("env");
	switch (env) {
		case "dev":
			return "https://alove-dev.algoaitech.io";
		case "staging":
			return "https://mujual-staging.algoaitech.io";
		case "production":
			return "https://mujual.algoaitech.io";
		default:
			return window.location.hostname === "localhost"
				? "https://alove-dev.algoaitech.io"
				: "https://mujual.algoaitech.io";
	}
};

const borderRadius = 5;
const size = "small";

function LandingPage(props) {
	const { setCompleted } = props;
	const classes = useStyles();

	useEffect(() => {
		getDropDownFieldData();
	}, []);

	const getDropDownFieldData = async () => {
		return new Promise((resolve, reject) => {
			landingLog("mm-form", "request-start", { url: `${getLambdaDomain()}/matchmakers/mmForm` });
			axios
				.get(`${getLambdaDomain()}/matchmakers/mmForm`, jsonOrTextResponse)
				.then((res) => {
					landingLog("mm-form", "response", { status: res?.status, dataType: typeof res?.data });
					setDropdownValues(res?.data);
					resolve();
				})
				.catch((error) => {
					landingLog(
						"mm-form",
						"request-failed",
						{
							message: error?.message,
							status: error?.response?.status,
						},
						"warn"
					);
					console.error("error from get logs:", error);
				});
		});
	};

	const [codeDialogStatus, setCodeDialogStatus] = useState(false);

	const [dropDownValues, setDropdownValues] = useState([]);
	const [loader, setLoader] = useState(false);

	const [formData, setFormData] = React.useState({
		title: null,
		firstName: null,
		lastName: null,
		prefereAge: null,
		singlesPlan: null,
		singlesType: null,
		experience: null,
		instructions: null,
		aboutMe: null,
		email: null,
		isNewEmail: true,
		phone: null,
		notification: {
			Email: true,
			SMS: false,
			WA: false,
		},
		communicationDirections: null,
		avatar: null,
		fileName: null,
	});

	const [inputErrors, setInputErrors] = React.useState({
		title: false,
		firstName: false,
		lastName: false,
		prefereAge: false,
		singlesPlan: false,
		singlesType: false,
		experience: false,
		instructions: false,
		aboutMe: false,
		email: false,
		phone: false,
		communicationDirections: false,
		ravsName: false,
	});

	const [accessCode, setAccessCode] = React.useState({
		val1: null,
		val2: null,
		val3: null,
		val4: null,
	});

	const [accessCodeErrors, setAccessCodeErrors] = React.useState({
		val1: false,
		val2: false,
		val3: false,
		val4: false,
	});

	const [verificatioCode] = useState(null);

	const checkEmail = () => {
		const nextErrors = { ...inputErrors };

		if (formData?.email) {
			nextErrors.email = isValidEmail(formData.email) ? false : "Invalid email";
		}
		if (formData?.phone) {
			if (!isValidPhoneInput(formData.phone)) {
				nextErrors.phone = "Invalid phone";
			} else {
				const normalizedPhone = normalizePhone(formData.phone);
				nextErrors.phone = isValidNormalizedPhone(normalizedPhone)
					? false
					: "Invalid phone";
			}
		}

		setInputErrors(nextErrors);
		if (nextErrors.email || nextErrors.phone) {
			return;
		}

		const emailForCheck = normalizeEmail(formData.email);
		const phoneForCheck = normalizePhone(formData.phone);

		setInputErrors({
			...nextErrors,
			email: "Checking email...",
			phone: "Checking phone...",
		});
		axios
			.get(
				`${getLambdaDomain()}/matchmakers/isUserExsits?email=${encodeURIComponent(
					emailForCheck
				)}&phone=${encodeURIComponent(phoneForCheck)}`,
				jsonOrTextResponse
			)
			.then((res) => {
				landingLog("mm-exists-check", "response", { status: res?.status, dataType: typeof res?.data });
				console.log("res: ", res);
				setInputErrors((prev) => ({
					...prev,
					email: res?.data?.emailUser ? "Email already exists" : false,
					phone: res?.data?.phoneUser ? "Phone already exists" : false,
				}));
			})
			.catch((error) => {
				landingLog(
					"mm-exists-check",
					"request-failed",
					{
						message: error?.message,
						status: error?.response?.status,
					},
					"warn"
				);
				console.error("check user:", error);
				// reject(error);
			});
	};

	const handleSubmit = () => {
		const normalizedEmail = normalizeEmail(formData?.email);
		const normalizedPhone = normalizePhone(formData?.phone);

		let errors = { ...inputErrors };
		formData?.title === null ? (errors.title = true) : (errors.title = false);
		formData?.firstName === null ? (errors.firstName = true) : (errors.firstName = false);
		formData?.lastName === null ? (errors.lastName = true) : (errors.lastName = false);
		formData?.prefereAge === null ? (errors.prefereAge = true) : (errors.prefereAge = false);
		formData?.singlesPlan === null ? (errors.singlesPlan = true) : (errors.singlesPlan = false);
		formData?.singlesType === null ? (errors.singlesType = true) : (errors.singlesType = false);
		formData?.experience === null ? (errors.experience = true) : (errors.experience = false);
		formData?.email === null ? (errors.email = true) : (errors.email = false);
		formData?.phone === null ? (errors.phone = true) : (errors.phone = false);
		formData?.ravsName === null ? (errors.ravsName = true) : (errors.ravsName = false);

		if (!normalizedEmail || !isValidEmail(normalizedEmail)) {
			errors.email = "Invalid email";
		}
		if (
			!normalizedPhone ||
			!isValidPhoneInput(formData?.phone) ||
			!isValidNormalizedPhone(normalizedPhone)
		) {
			errors.phone = "Invalid phone";
		}

		setInputErrors(errors);

		if (errors.email || errors.phone || Object.keys(errors).find((i) => errors[i] === true)) {
			alert("Please fill all the required fields");
			return;
		}

		setFormData((prev) => ({
			...prev,
			email: normalizedEmail,
			phone: normalizedPhone,
		}));

		// console.log('err:', err);
		if (!Object.keys(errors).find((i) => errors[i] === true)) {
			setLoader(true);
			let obj = {};
			obj.brand_id = 102;
			obj.first_name = formData?.firstName;
			obj.last_name = formData?.lastName;
			obj.short_description = formData?.aboutMe;
			obj.population = {
				singlePlans: formData?.singlesPlan,
				singleTypes: formData?.singlesType,
				singleAges: formData?.prefereAge,
			};
			obj.email = normalizedEmail;
			obj.phone = normalizedPhone;
			obj.location = {
				latitude: 0,
				longitude: 0,
			};
			obj.attributes = {
				title: formData?.title,
				experience: formData?.experience,
				special_intstructions: formData?.instructions,
				notification: formData?.notification,
				communicationDirection: formData?.communicationDirections,
				avatarBase64: formData?.avatar,
				listOrgs: formData?.listOrgs,
				ravsName: formData?.ravsName,
			};
			landingLog("mm-submit", "request-start", {
				url: `${getLambdaDomain()}/matchmakers/matchmakers`,
				hasAvatar: !!obj?.attributes?.avatarBase64,
			});
			axios
				.post(`${getLambdaDomain()}/matchmakers/matchmakers`, obj, jsonOrTextResponse)
				.then((res) => {
					landingLog("mm-submit", "http-ok", {
						status: res?.status,
						dataType: typeof res?.data,
						dataHead:
							typeof res?.data === "string" ? String(res.data).slice(0, 80) : undefined,
					});
					console.log("res: ", res);
					if (res?.status >= 200 && res?.status < 300) {
						setCompleted(true);
					} else {
						landingLog("mm-submit", "unexpected-http-status", { status: res?.status });
						alert("Unexpected response from server. Please contact support if this continues.");
					}
				})
				.catch((error) => {
					const corsGuess = isLikelyCorsOrOpaqueNetworkReadBlocked(error);
					landingLog(
						"mm-submit",
						corsGuess ? "request-failed-likely-cors" : "request-failed",
						{
							message: error?.message,
							responseStatus: error?.response?.status,
							code: error?.code,
							pageOrigin: typeof window !== "undefined" ? window.location.origin : "",
						},
						corsGuess ? "warn" : "error"
					);
					if (corsGuess) {
						setCompleted(true);
					} else {
						alert("Error while submitting the form, please fill all the required fields");
					}
					console.error("error from deploy settings:", error);
				})
				.finally(() => setLoader(false));
		}
	};

	const sendPost = async () => {
		setLoader(true);
		let obj = {};
		obj.brand_id = 102;
		obj.first_name = formData?.firstName;
		obj.last_name = formData?.lastName;
		obj.short_description = formData?.aboutMe;
		obj.population = {
			singlePlans: formData?.singlesPlan,
			singleTypes: formData?.singleTypes,
		};
		obj.email = normalizeEmail(formData?.email);
		obj.phone = normalizePhone(formData?.phone);
		obj.location = {
			latitude: 0,
			longitude: 0,
		};
		obj.attributes = {
			title: formData?.title,
			experience: formData?.experience,
			special_intstructions: formData?.instructions,
			notification: formData?.notification,
			communicationDirection: formData?.communicationDirections,
			avatarBase64: formData?.avatar,
		};

		// console.log('obj before send: ', obj);

		landingLog("mm-submit-dialog", "request-start", {
			url: `${getLambdaDomain()}/matchmakers/matchmakers`,
			hasAvatar: !!obj?.attributes?.avatarBase64,
		});
		axios
			.post(`${getLambdaDomain()}/matchmakers/matchmakers`, obj, jsonOrTextResponse)
			.then((res) => {
				landingLog("mm-submit-dialog", "http-ok", {
					status: res?.status,
					dataType: typeof res?.data,
				});
				console.log("res: ", res);
				if (res?.status >= 200 && res?.status < 300) {
					setCompleted(true);
				}
			})
			.catch((error) => {
				const corsGuess = isLikelyCorsOrOpaqueNetworkReadBlocked(error);
				landingLog(
					"mm-submit-dialog",
					corsGuess ? "request-failed-likely-cors" : "request-failed",
					{
						message: error?.message,
						responseStatus: error?.response?.status,
						pageOrigin: typeof window !== "undefined" ? window.location.origin : "",
					},
					corsGuess ? "warn" : "warn"
				);
				if (corsGuess) {
					setCompleted(true);
				}
				console.error("error from deploy settings:", error);
			})
			.finally(() => setLoader(false));
	};

	const sendMessage = async () => {
		let sendData;
		// if (preferedMethod[0] === "Phone") {
		// 	sendData = {
		// 		brandId: 102,
		// 		phoneNumber: formData?.phone,
		// 	};
		// } else {
		// 	sendData = {
		// 		brandId: 102,
		// 		emailAddress: formData?.email,
		// 	};
		// }
		await axios
			.post(`${getLambdaDomain()}/auth/sendCode`, sendData)
			.then((res) => {
				console.log("res: ", res);
				console.log("res: ", res?.data?.code);
				if (res?.status === 200) {
				}
				// resolve(res);
			})
			.catch((error) => {
				console.error("error from deploy settings:", error);
				// reject(error);
			});
	};

	const uploadFile = async (e) => {
		console.log("e?.target?.files[0]: ", e?.target?.files[0]);
		await getBase64(e?.target?.files[0]);
	};

	const getBase64 = (file) => {
		return new Promise((resolve) => {
			let baseURL = "";
			let reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				baseURL = reader.result;
				resolve(baseURL);
				setFormData({ ...formData, avatar: baseURL, fileName: file?.name });
			};
		});
	};

	return (
		<Grid container
			style={{
				maxWidth: "1000px"

			}}>
			<FormCard
			>
				<CodeDialog
					codeDialogStatus={codeDialogStatus}
					setCodeDialogStatus={setCodeDialogStatus}
					accessCode={accessCode}
					setAccessCode={setAccessCode}
					accessCodeErrors={accessCodeErrors}
					setAccessCodeErrors={setAccessCodeErrors}
					verificatioCode={verificatioCode}
					formData={formData}
					preferdMethod={[]}
					sendMessageDO={sendMessage}
					sendPostDO={sendPost}
				/>

				<Header />

				<BasicDetails
					formData={formData}
					setFormData={setFormData}
					inputErrors={inputErrors}
					dropDownValues={dropDownValues}
					size={size}
					borderRadius={borderRadius}
				/>
			</FormCard>

			<FormCard
			>
				<ContactDetails
					formData={formData}
					setFormData={setFormData}
					inputErrors={inputErrors}
					checkEmail={checkEmail}
					size={size}
					borderRadius={borderRadius}
				/>

				<AdditionalDetails
					formData={formData}
					setFormData={setFormData}
					inputErrors={inputErrors}
					size={size}
					borderRadius={borderRadius}
				/>

				<NotificationPreferences
					formData={formData}
					setFormData={setFormData}
				/>
			</FormCard>

			<FormCard
			>
				<ProfileImage
					formData={formData}
					uploadFile={uploadFile}
					classes={classes}
				/>
			</FormCard>
			<Footer
				handleSubmit={handleSubmit}
				loader={loader}
				formData={formData}
				classes={classes}
			/>
		</Grid>
	);
}

export default LandingPage;
